import { account } from './account.js';
import { device } from './device.js';

/* Anuncios de vídeo con el SDK de Google (IMA) — PROTOTIPO, solo web. Apagado salvo que el servidor lo active
   (ADS_ENABLED=1; ver server/api.js). La etiqueta (VAST) y los momentos los fija el servidor en /api/config:

     account.ads = { tag, preroll, midrollSec, live, scope, minGapSec, skipAdmins }

   `scope`: 'archive' (por defecto) = solo en el cine clásico de Internet Archive (core/archive.js, contenido con derechos libres);
   'all' = en todo lo que se reproduzca.

   Pensado para quitarse con Premium más adelante. Nunca bloquea la reproducción: si el SDK no carga (bloqueador de
   anuncios, sin red), la etiqueta no devuelve anuncio o tarda demasiado, el contenido empieza igualmente. */

const SDK = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
const START_TIMEOUT = 10000; // sin que empiece el anuncio
const HARD_TIMEOUT = 180000; // tope de un anuncio entero

/** Motivo de que un anuncio no salga (etiqueta mal, sin anuncio disponible, bloqueador...): útil al configurar. */
const warn = (e) => { try { const er = e.getError(); console.warn('[ads]', er.getErrorCode(), er.getMessage()); } catch (x) { console.warn('[ads] error'); } };

const IOS = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

let sdk = null;
function loadSdk() {
    if (window.google && window.google.ima) return Promise.resolve();
    if (!sdk) {
        sdk = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = SDK;
            s.async = true;
            const timer = setTimeout(() => reject(new Error('timeout')), 8000);
            s.onload = () => { clearTimeout(timer); window.google && window.google.ima ? resolve() : reject(new Error('sdk')); };
            s.onerror = () => { clearTimeout(timer); reject(new Error('blocked')); };
            document.head.appendChild(s);
        }).catch((e) => { sdk = null; throw e; });
    }
    return sdk;
}

/** La etiqueta con el `correlator` de esta petición (Google lo pide distinto en cada una). */
export function tagFor(tag, now = Date.now()) {
    return /([?&])correlator=(?=&|$)/.test(tag) ? tag.replace(/([?&])correlator=(?=&|$)/, '$1correlator=' + now) : tag;
}

export const ads = {
    lastEnd: 0,
    _stop: null,

    get cfg() { return (account && account.ads && account.ads.tag) ? account.ads : null; },

    /** ¿Hay que poner anuncios en esta reproducción? mode: 'vod' | 'episode' | 'live'; free: es contenido de derechos libres. */
    enabledFor(mode, free = false) {
        const c = this.cfg;
        if (!c || device.isTV || device.isCordova || device.isElectron) return false;
        if (c.skipAdmins && account.isAdmin) return false;
        if (c.scope !== 'all' && !free) return false;
        return mode === 'live' ? !!c.live : true;
    },

    /** Respeta el intervalo mínimo entre anuncios (sobre todo para no repetir al cambiar de canal). */
    gapOk(now = Date.now()) {
        const c = this.cfg;
        return !c || !c.minGapSec || now - this.lastEnd >= c.minGapSec * 1000;
    },

    get active() { return !!this._stop; },

    /** Corta el anuncio en curso (cerrar el reproductor). */
    stop() { if (this._stop) this._stop(false); },

    /**
     * Reproduce un anuncio en `container` (encima del vídeo). Siempre termina, nunca falla: devuelve true si se llegó a
     * mostrar un anuncio. `video` es el vídeo del contenido (el SDK lo usa para el seguimiento).
     */
    async play({ container, video }) {
        const c = this.cfg;
        if (!c || this._stop) return false;
        try { await loadSdk(); } catch (e) { return false; }
        const ima = window.google.ima;
        return new Promise((resolve) => {
            let done = false;
            let started = false;
            let manager = null;
            const slot = document.createElement('div');
            slot.className = 'ad-slot';
            container.appendChild(slot);
            container.hidden = false;
            let displayContainer;
            let loader;
            const size = () => ({ w: Math.max(200, container.clientWidth), h: Math.max(120, container.clientHeight) });
            const onResize = () => { if (manager) { const s = size(); try { manager.resize(s.w, s.h, ima.ViewMode.NORMAL); } catch (e) { /* ya cerrado */ } } };
            const guardStart = setTimeout(() => { if (!started) finish(false); }, START_TIMEOUT);
            const guardHard = setTimeout(() => finish(started), HARD_TIMEOUT);
            const finish = (shown) => {
                if (done) return;
                done = true;
                clearTimeout(guardStart);
                clearTimeout(guardHard);
                window.removeEventListener('resize', onResize);
                this._stop = null;
                try { if (manager) manager.destroy(); } catch (e) { /* nada */ }
                try { if (loader) loader.destroy(); } catch (e) { /* nada */ }
                try { if (displayContainer) displayContainer.destroy(); } catch (e) { /* nada */ }
                slot.remove();
                container.hidden = true;
                if (shown) this.lastEnd = Date.now();
                resolve(!!shown);
            };
            this._stop = finish;
            window.addEventListener('resize', onResize);
            try {
                // Solo iOS necesita el vídeo del contenido para reproducir el anuncio; en el resto, el SDK pone el suyo y no se toca el
                // del contenido (si se le pasa, puede dejarlo en pausa al terminar).
                displayContainer = IOS ? new ima.AdDisplayContainer(slot, video) : new ima.AdDisplayContainer(slot);
                displayContainer.initialize();
                loader = new ima.AdsLoader(displayContainer);
                loader.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (ev) => {
                    try {
                        manager = IOS ? ev.getAdsManager(video) : ev.getAdsManager();
                        manager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (e) => { warn(e); finish(started); });
                        manager.addEventListener(ima.AdEvent.Type.STARTED, () => { started = true; });
                        manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => finish(true));
                        manager.addEventListener(ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => finish(started));
                        const s = size();
                        manager.init(s.w, s.h, ima.ViewMode.NORMAL);
                        manager.start();
                    } catch (e) { finish(false); }
                });
                loader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, (e) => { warn(e); finish(false); });
                const req = new ima.AdsRequest();
                req.adTagUrl = tagFor(c.tag);
                const s = size();
                req.linearAdSlotWidth = s.w;
                req.linearAdSlotHeight = s.h;
                req.nonLinearAdSlotWidth = s.w;
                req.nonLinearAdSlotHeight = Math.round(s.h / 3);
                loader.requestAds(req);
            } catch (e) {
                finish(false);
            }
        });
    }
};
